import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Heart, Car, Pill, ShoppingCart, Shirt, School, Package, 
  Baby, UtensilsCrossed, Coffee, Shield, CheckCircle, Clock,
  AlertTriangle, MessageCircle, ChevronDown, ChevronRight
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUEST_ICONS = {
  lift: Car,
  pharmacy: Pill,
  groceries: ShoppingCart,
  laundry: Shirt,
  school_run: School,
  errand: Package,
  childcare: Baby,
  meal: UtensilsCrossed,
  company: Coffee
};

const MummaMate = () => {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [requestTypes, setRequestTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [terms, setTerms] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRequest, setNewRequest] = useState({
    request_type: "",
    description: "",
    location_area: ""
  });
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eligRes, typesRes, reqRes, termsStatusRes] = await Promise.all([
        axios.get(`${API}/mummamate/eligibility`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mummamate/types`),
        axios.get(`${API}/mummamate/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/legal/mummamate-status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setEligibility(eligRes.data);
      setRequestTypes(typesRes.data.request_types || []);
      setRequests(reqRes.data.requests || []);
      setTermsAccepted(termsStatusRes.data.accepted);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/legal/mummamate-terms`);
      setTerms(response.data);
      setShowTerms(true);
    } catch (error) {
      toast.error("Failed to load terms");
    }
  };

  const acceptTerms = async () => {
    try {
      await axios.post(`${API}/legal/accept-mummamate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTermsAccepted(true);
      setShowTerms(false);
      toast.success("Terms accepted! You can now use MummaMate 💜");
    } catch (error) {
      toast.error("Failed to accept terms");
    }
  };

  const createRequest = async () => {
    if (!newRequest.request_type || !newRequest.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/mummamate/requests`, newRequest, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Request posted! Your matches have been notified 💜");
      setShowCreateForm(false);
      setNewRequest({ request_type: "", description: "", location_area: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    }
  };

  const offerHelp = async (requestId) => {
    try {
      await axios.post(`${API}/mummamate/requests/${requestId}/offer`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("You've offered to help! Message them to coordinate 💜");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to offer help");
    }
  };

  const completeRequest = async (requestId) => {
    try {
      await axios.post(`${API}/mummamate/requests/${requestId}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Request completed! Thanks for being part of the village 💜");
      fetchData();
    } catch (error) {
      toast.error("Failed to complete request");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="fill-current" size={32} />
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>
              MummaMate™
            </h1>
          </div>
          <p className="text-white/90">
            Because mums and caregivers aren't robots. Sometimes we need a little help too! 💜
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Eligibility Check */}
        {!eligibility?.is_eligible ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Unlock MummaMate</h3>
                <p className="text-gray-600 mb-4">{eligibility?.reason}</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    {eligibility?.successful_meetups >= 1 ? (
                      <CheckCircle className="text-green-500" size={16} />
                    ) : (
                      <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                    )}
                    <span>Complete 1 successful meetup</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {eligibility?.has_trusted_badge ? (
                      <CheckCircle className="text-green-500" size={16} />
                    ) : (
                      <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                    )}
                    <span>OR earn the Trusted Family Badge</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !termsAccepted ? (
          /* Terms Acceptance */
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Accept Terms to Continue</h3>
                <p className="text-gray-600 mb-4">
                  Before using MummaMate, please read and accept our terms. This protects everyone in our community.
                </p>
                <Button onClick={fetchTerms}>
                  Read & Accept Terms
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Create Request Button */}
            <Button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="w-full mb-6 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
              size="lg"
            >
              <Heart className="mr-2" size={20} />
              {showCreateForm ? "Cancel" : "Ask for Help"}
            </Button>

            {/* Create Request Form */}
            {showCreateForm && (
              <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">What do you need help with?</h3>
                
                {/* Request Type Selection */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {requestTypes.map((type) => {
                    const Icon = REQUEST_ICONS[type.id] || Package;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setNewRequest({ ...newRequest, request_type: type.id })}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                          newRequest.request_type === type.id
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 hover:border-purple-300"
                        }`}
                      >
                        <span className="text-2xl">{type.emoji}</span>
                        <span className="text-xs font-medium text-gray-700">{type.name}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tell us more *
                  </label>
                  <textarea
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                    placeholder="e.g., I need a lift to a birthday party in Croydon on Saturday at 2pm"
                    className="w-full p-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                {/* Location */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area (optional)
                  </label>
                  <Input
                    value={newRequest.location_area}
                    onChange={(e) => setNewRequest({ ...newRequest, location_area: e.target.value })}
                    placeholder="e.g., Croydon, South London"
                  />
                </div>

                <Button onClick={createRequest} className="w-full">
                  Post Request
                </Button>
              </div>
            )}

            {/* Request Types Info */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
              <h3 className="font-bold text-gray-900 mb-3">Ways to Help Each Other</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {requestTypes.map((type) => (
                  <div key={type.id} className="flex items-center gap-2 text-sm text-gray-600">
                    <span>{type.emoji}</span>
                    <span>{type.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requests List */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-900">Recent Requests</h3>
              
              {requests.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No requests yet. Be the first to ask for help!</p>
                </div>
              ) : (
                requests.map((req) => {
                  const type = requestTypes.find(t => t.id === req.request_type);
                  return (
                    <div
                      key={req.request_id}
                      className={`bg-white rounded-2xl shadow-sm p-4 border-l-4 ${
                        req.is_mine ? "border-purple-500" : "border-pink-500"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{type?.emoji || "📦"}</span>
                          <div>
                            <span className="font-medium text-gray-900">
                              {req.is_mine ? "Your Request" : req.requester_name}
                            </span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                              req.status === "open" ? "bg-green-100 text-green-700" :
                              req.status === "helping" ? "bg-blue-100 text-blue-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {req.status}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-3">{req.description}</p>
                      
                      {req.location_area && (
                        <p className="text-sm text-gray-500 mb-3">📍 {req.location_area}</p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        {!req.is_mine && req.status === "open" && (
                          <>
                            <Button size="sm" onClick={() => offerHelp(req.request_id)}>
                              <Heart size={14} className="mr-1" /> I Can Help
                            </Button>
                            <Button size="sm" variant="outline">
                              <MessageCircle size={14} className="mr-1" /> Message
                            </Button>
                          </>
                        )}
                        {req.is_mine && req.status === "helping" && (
                          <Button size="sm" onClick={() => completeRequest(req.request_id)}>
                            <CheckCircle size={14} className="mr-1" /> Mark Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Terms Modal */}
      {showTerms && terms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b bg-purple-50">
              <h2 className="text-xl font-bold text-gray-900">{terms.title}</h2>
              <p className="text-sm text-gray-600">Version {terms.version} • {terms.effective_date}</p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {terms.sections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-2">{section.title}</h3>
                  <p className="text-gray-700 text-sm">{section.content}</p>
                </div>
              ))}
            </div>
            <div className="p-6 border-t bg-gray-50">
              <p className="text-sm text-gray-600 mb-4">{terms.acceptance_text}</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowTerms(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={acceptTerms} className="flex-1 bg-purple-600 hover:bg-purple-700">
                  I Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default MummaMate;
